import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const ANGLE_SEQUENCE = ['top', 'front', 'side'];
const CAMERA_BASE_CONSTRAINTS = {
  width: { ideal: 1280 },
  height: { ideal: 720 }
};

function stopMediaStream(stream) {
  if (!stream) return;
  stream.getTracks().forEach((track) => {
    try {
      track.stop();
    } catch (err) {
      // no-op
    }
  });
}

function ensureCanvas() {
  const canvas = document.createElement('canvas');
  canvas.width = 1920;
  canvas.height = 1080;
  return canvas;
}

function resizeToCanvas(video, canvas) {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Unable to capture frame from camera.');
  }

  const maxDimension = 1920;
  const { videoWidth, videoHeight } = video;

  if (!videoWidth || !videoHeight) {
    throw new Error('Camera not ready. Please wait a moment and try again.');
  }

  let targetWidth = videoWidth;
  let targetHeight = videoHeight;

  if (Math.max(videoWidth, videoHeight) > maxDimension) {
    const scale = maxDimension / Math.max(videoWidth, videoHeight);
    targetWidth = Math.round(videoWidth * scale);
    targetHeight = Math.round(videoHeight * scale);
  }

  canvas.width = targetWidth;
  canvas.height = targetHeight;
  ctx.drawImage(video, 0, 0, targetWidth, targetHeight);
}

function buildCameraConstraintCandidates(desiredFacing) {
  const candidates = [];

  if (desiredFacing) {
    candidates.push({
      audio: false,
      video: {
        ...CAMERA_BASE_CONSTRAINTS,
        facingMode: { exact: desiredFacing }
      }
    });
    candidates.push({
      audio: false,
      video: {
        ...CAMERA_BASE_CONSTRAINTS,
        facingMode: { ideal: desiredFacing }
      }
    });
  }

  candidates.push({
    audio: false,
    video: { ...CAMERA_BASE_CONSTRAINTS }
  });

  candidates.push({
    audio: false,
    video: true
  });

  return candidates;
}

async function openCameraStream(desiredFacing) {
  if (!navigator?.mediaDevices?.getUserMedia) {
    throw Object.assign(new Error('Camera API unavailable'), { name: 'CameraAPIUnavailable' });
  }

  const candidates = buildCameraConstraintCandidates(desiredFacing);
  let lastError = null;

  for (const constraints of candidates) {
    try {
      console.info('[useMealPhotoCapture] getUserMedia attempt', constraints);
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      const [track] = stream.getVideoTracks();
      const settings = track?.getSettings?.();
      const facingMode = settings?.facingMode || desiredFacing || null;
      console.info('[useMealPhotoCapture] getUserMedia success', { facingMode, settings });
      return { stream, facingMode };
    } catch (err) {
      console.warn('[useMealPhotoCapture] getUserMedia failed', { constraints, err });
      lastError = err;
    }
  }

  throw lastError || new Error('Unable to access camera stream.');
}

export function useMealPhotoCapture() {
  const streamRef = useRef(null);
  const facingModeRef = useRef('environment');
  const [stream, setStream] = useState(null);
  const [facingMode, setFacingMode] = useState('environment');
  const [isInitializing, setIsInitializing] = useState(false);
  const [isCameraUnavailable, setIsCameraUnavailable] = useState(false);
  const [error, setError] = useState(null);
  const [currentAngleIndex, setCurrentAngleIndex] = useState(0);
  const [captures, setCaptures] = useState(() => ({
    top: null,
    front: null,
    side: null
  }));

  const canvasRef = useRef(null);
  const angleOrder = useMemo(() => ANGLE_SEQUENCE.slice(), []);

  const currentAngle = angleOrder[currentAngleIndex] || null;
  const hasAllCaptures = angleOrder.every((angle) => Boolean(captures[angle]));

  const startCamera = useCallback(
    async (overrideFacingMode) => {
      const targetFacing = overrideFacingMode || facingModeRef.current || 'environment';
      if (streamRef.current && !overrideFacingMode) {
        console.info('[useMealPhotoCapture] Reusing existing stream');
        setIsCameraUnavailable(false);
        setIsInitializing(false);
        return streamRef.current;
      }

      setIsInitializing(true);
      setError(null);

      console.info('[useMealPhotoCapture] startCamera', {
        overrideFacingMode,
        currentFacing: facingModeRef.current,
        streamExists: Boolean(streamRef.current)
      });

      const attemptOrder = [];
      if (targetFacing) {
        attemptOrder.push(targetFacing);
      }
      if (targetFacing === 'environment') {
        attemptOrder.push('user');
      } else if (targetFacing === 'user') {
        attemptOrder.push('environment');
      }
      if (attemptOrder.length === 0) {
        attemptOrder.push('environment', 'user');
      }

      for (const mode of attemptOrder) {
        try {
          const preview = await openCameraStream(mode);
          streamRef.current = preview.stream;
          setStream(preview.stream);
          setIsCameraUnavailable(false);
          const chosenFacing = preview.facingMode || mode || 'user';
          facingModeRef.current = chosenFacing;
          setFacingMode(chosenFacing);
          setIsInitializing(false);
          console.info('[useMealPhotoCapture] camera established', { selectedFacing: preview.facingMode || mode });
          return preview.stream;
        } catch (err) {
          console.warn('[useMealPhotoCapture] Failed to start camera with mode:', mode, err);
        }
      }

      setIsCameraUnavailable(true);
      console.error('[useMealPhotoCapture] Unable to establish camera stream');
      setError('Unable to access the camera on this device. Please check permissions or switch devices.');
      setIsInitializing(false);
      streamRef.current = null;
      setStream(null);
      return null;
    },
    []
  );

  const stopCamera = useCallback(() => {
    stopMediaStream(streamRef.current);
    streamRef.current = null;
    setStream(null);
  }, []);

  const toggleCamera = useCallback(async () => {
    const current = facingModeRef.current || 'environment';
    const nextFacing = current === 'environment' ? 'user' : 'environment';
    console.info('[useMealPhotoCapture] toggleCamera', { currentFacing: current, nextFacing });
    stopCamera();
    await startCamera(nextFacing);
  }, [startCamera, stopCamera]);

  const captureCurrentAngle = useCallback(
    async (videoEl) => {
      if (!videoEl) {
        setError('Camera preview is not ready.');
        return null;
      }

      try {
        if (!canvasRef.current) {
          canvasRef.current = ensureCanvas();
        }

        resizeToCanvas(videoEl, canvasRef.current);
        console.info('[useMealPhotoCapture] Capturing angle', { angle: currentAngle, videoWidth: videoEl.videoWidth, videoHeight: videoEl.videoHeight });
        const dataUrl = canvasRef.current.toDataURL('image/jpeg', 0.88);

        const angle = currentAngle;
        if (!angle) {
          throw new Error('Capture sequence is complete.');
        }

        setCaptures((prev) => ({
          ...prev,
          [angle]: dataUrl
        }));

        setCurrentAngleIndex((prev) => Math.min(prev + 1, angleOrder.length));

        return dataUrl;
      } catch (err) {
        console.error('[useMealPhotoCapture] capture failed', err);
        setError(err.message || 'Failed to capture photo. Please try again.');
        return null;
      }
    },
    [angleOrder.length, currentAngle]
  );

  const retakeAngle = useCallback(
    (angleKey) => {
      if (!angleOrder.includes(angleKey)) {
        return;
      }
      setError(null);
      setCaptures((prev) => ({
        ...prev,
        [angleKey]: null
      }));
      setCurrentAngleIndex(angleOrder.indexOf(angleKey));
    },
    [angleOrder]
  );

  const reset = useCallback(() => {
    setCaptures({
      top: null,
      front: null,
      side: null
    });
    setCurrentAngleIndex(0);
    setError(null);
  }, []);

  useEffect(() => {
    canvasRef.current = ensureCanvas();
    return () => {
      stopCamera();
      canvasRef.current = null;
    };
  }, [stopCamera]);

  return {
    stream,
    facingMode,
    isInitializing,
    isCameraUnavailable,
    error,
    currentAngle,
    angleOrder,
    captures,
    hasAllCaptures,
    startCamera,
    stopCamera,
    toggleCamera,
    captureCurrentAngle,
    retakeAngle,
    reset,
    setError
  };
}

export function getAnglePrompt(angle) {
  switch (angle) {
    case 'top':
      return 'Hold the camera above the meal so the entire plate is visible.';
    case 'front':
      return 'Show the meal straight-on at plate level.';
    case 'side':
      return 'Capture a side angle to highlight height or layers.';
    default:
      return '';
  }
}
